/**
 * SVG Gauge Mathematical Helper Functions
 */

export interface Point {
  x: number
  y: number
}

/**
 * Converts polar coordinates (radius, angle in degrees) to cartesian coordinates (x, y)
 * relative to a custom center point (cx, cy).
 * Note: SVGs have the Y-axis increasing downwards.
 * 0 degrees points directly East (right), 90 points South (down),
 * 180 points West (left), 270 points North (up).
 */
export function polarToCartesian(cx: number, cy: number, r: number, angleInDegrees: number): Point {
  const angleInRadians = (angleInDegrees * Math.PI) / 180.0
  return {
    x: cx + r * Math.cos(angleInRadians),
    y: cy + r * Math.sin(angleInRadians),
  }
}

/**
 * Maps a sensor angle (from -30 to +30 degrees) to a physical SVG rotation angle (in degrees).
 * We center the gauge at 270 degrees (straight up/North corresponding to 0° sensor angle).
 * The user can adjust the sweep angle (defaulting to 270 degrees).
 *
 * For a 270° sweep:
 * - -30° sensor angle maps to 270 - (30 * 4.5) = 135° (bottom-left)
 * - +30° sensor angle maps to 270 + (30 * 4.5) = 405° (bottom-right)
 * - 0° sensor angle maps to 270° (top-center)
 */
export function sensorAngleToPhysicalAngle(
  sensorAngle: number,
  sweepAngle: number = 270,
  rangeMin: number = -30,
  rangeMax: number = 30,
): number {
  const centerAngle = 270 // 12 o'clock is 270 degrees
  const angleRange = rangeMax - rangeMin // 60 degrees
  const scale = sweepAngle / angleRange // 270 / 60 = 4.5

  return centerAngle + sensorAngle * scale
}

/**
 * Generates the SVG Path string (d) for an arc given the center, radius,
 * start orientation (in degrees), and end orientation (in degrees).
 */
export function describeArc(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
): string {
  const start = polarToCartesian(cx, cy, r, startAngle)
  const end = polarToCartesian(cx, cy, r, endAngle)

  // Normalize angle difference to positive to decide large-arc flag
  let diff = endAngle - startAngle
  while (diff < 0) diff += 360
  const largeArcFlag = diff > 180 ? 1 : 0
  const sweepFlag = 1 // Always sweep clockwise

  return ['M', start.x, start.y, 'A', r, r, 0, largeArcFlag, sweepFlag, end.x, end.y].join(' ')
}
